import { motion, useInView } from "framer-motion";
import { useRef, useState, memo } from "react";
import { Users, Award, MapPin, Globe2, Calendar } from "lucide-react";
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
  ZoomableGroup,
} from "react-simple-maps";
import { useLanguage } from "../../context/LanguageContext";

const FOUNDED_YEAR = 2012;
const CURRENT_YEAR = new Date().getFullYear();
const YEARS = CURRENT_YEAR - FOUNDED_YEAR;

// Public TopoJSON of world countries (Natural Earth, simplified)
const GEO_URL = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-50m.json";

// ISO numeric codes used by world-atlas
const ACTIVE_COUNTRY_IDS = {
  484: "MX", // Mexico
  170: "CO", // Colombia
  222: "SV", // El Salvador
};

const COLORS = {
  MX: "#F59E0B",
  CO: "#2ED3C6",
  SV: "#2ED3C6",
  inactive: "#D7DEE6",
  border: "#0B1F3B",
};

const translations = {
  overline: { es: "Trayectoria", en: "Track Record" },
  title: {
    es: `+${YEARS} años formando talento MICE en Latinoamérica`,
    en: `+${YEARS} years training MICE talent across Latin America`,
  },
  subtitle: {
    es: `Desde ${FOUNDED_YEAR}, nuestro programa insignia se ha impartido en más de 20 destinos estratégicos. Hoy llevamos esa trayectoria al formato online, accesible desde cualquier país.`,
    en: `Since ${FOUNDED_YEAR}, our flagship program has been delivered in over 20 strategic destinations. Today we bring that track record to an online format, accessible from any country.`,
  },
  stats: {
    participants: { es: "Participantes", en: "Participants" },
    graduates: { es: "Aprobados", en: "Graduates" },
    destinations: { es: "Destinos estratégicos", en: "Strategic destinations" },
    countries: { es: "Países", en: "Countries" },
  },
  mapTitle: { es: "Nuestra presencia en Latinoamérica", en: "Our presence across Latin America" },
  mapSubtitle: {
    es: "Programa impartido presencialmente en 3 países y 20+ ciudades.",
    en: "Program delivered in-person across 3 countries and 20+ cities.",
  },
  flagshipBadge: { es: "Sede principal", en: "Flagship" },
  citiesLabel: { es: "ciudades", en: "cities" },
  cityLabel: { es: "ciudad", en: "city" },
  nationwide: { es: "Programa impartido a nivel nacional", en: "Program delivered nationwide" },
};

// Real geographic coordinates [longitude, latitude]
const COUNTRIES = [
  {
    code: "MX",
    flag: "🇲🇽",
    name: { es: "México", en: "Mexico" },
    flagship: true,
    color: COLORS.MX,
    cities: [
      { name: "Ciudad de México", coords: [-99.1332, 19.4326] },
      { name: "Cancún", coords: [-86.8515, 21.1619] },
      { name: "Puerto Vallarta", coords: [-105.2253, 20.6534] },
      { name: "Hermosillo", coords: [-110.9559, 29.0729] },
      { name: "Tijuana", coords: [-117.0382, 32.5149] },
      { name: "Ciudad Juárez", coords: [-106.4245, 31.6904] },
      { name: "Chihuahua", coords: [-106.0691, 28.6353] },
      { name: "Pachuca (Hidalgo)", coords: [-98.7591, 20.1011] },
      { name: "Mérida (Yucatán)", coords: [-89.5926, 20.9674] },
      { name: "Zacatecas", coords: [-102.5832, 22.7709] },
      { name: "Celaya", coords: [-100.8166, 20.5230] },
      { name: "Durango", coords: [-104.6532, 24.0277] },
      { name: "Villahermosa (Tabasco)", coords: [-92.9475, 17.9892] },
      { name: "Oaxaca", coords: [-96.7266, 17.0732] },
      { name: "Campeche", coords: [-90.5349, 19.8301] },
      { name: "Ciudad del Carmen", coords: [-91.8273, 18.6402] },
    ],
  },
  {
    code: "SV",
    flag: "🇸🇻",
    name: { es: "El Salvador", en: "El Salvador" },
    flagship: false,
    color: COLORS.SV,
    nationwide: true,
    cities: [{ name: "San Salvador", coords: [-89.2182, 13.6929] }],
  },
  {
    code: "CO",
    flag: "🇨🇴",
    name: { es: "Colombia", en: "Colombia" },
    flagship: false,
    color: COLORS.CO,
    cities: [
      { name: "Medellín", coords: [-75.5812, 6.2476] },
      { name: "Barranquilla", coords: [-74.7813, 10.9685] },
    ],
  },
];

const STATS = [
  { icon: Users, value: "+1,100", key: "participants", color: "#2ED3C6" },
  { icon: Award, value: "+800", key: "graduates", color: "#F59E0B" },
  { icon: MapPin, value: "+20", key: "destinations", color: "#2ED3C6" },
  { icon: Globe2, value: "3", key: "countries", color: "#F59E0B" },
];

const MapView = memo(({ activeCountry, onHoverCountry }) => {
  const [hoveredCity, setHoveredCity] = useState(null);

  return (
    <div className="relative w-full" data-testid="region-map">
      <ComposableMap
        projection="geoMercator"
        projectionConfig={{ scale: 600, center: [-90, 12] }}
        width={800}
        height={650}
        style={{ width: "100%", height: "auto" }}
      >
        <ZoomableGroup zoom={1} center={[-90, 12]} disablePanning>
          <Geographies geography={GEO_URL}>
            {({ geographies }) =>
              geographies.map((geo) => {
                const id = parseInt(geo.id, 10);
                const code = ACTIVE_COUNTRY_IDS[id];
                const isActive = !!code;
                const isHovered = activeCountry === code;
                const fill = isActive
                  ? code === "MX"
                    ? COLORS.MX
                    : COLORS.CO
                  : COLORS.inactive;

                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    onMouseEnter={() => isActive && onHoverCountry(code)}
                    onMouseLeave={() => isActive && onHoverCountry(null)}
                    style={{
                      default: {
                        fill,
                        stroke: COLORS.border,
                        strokeWidth: isActive ? 0.6 : 0.3,
                        strokeOpacity: isActive ? 0.4 : 0.15,
                        outline: "none",
                        transition: "all 0.3s ease",
                        opacity: isActive ? (isHovered ? 1 : 0.85) : 0.45,
                      },
                      hover: {
                        fill,
                        stroke: COLORS.border,
                        strokeWidth: isActive ? 0.8 : 0.3,
                        strokeOpacity: 0.5,
                        outline: "none",
                        opacity: isActive ? 1 : 0.45,
                        cursor: isActive ? "pointer" : "default",
                      },
                      pressed: { fill, outline: "none" },
                    }}
                  />
                );
              })
            }
          </Geographies>

          {/* City markers */}
          {COUNTRIES.flatMap((country) =>
            country.cities.map((city) => {
              const key = `${country.code}-${city.name}`;
              const isHovered = hoveredCity === key;
              return (
                <Marker
                  key={key}
                  coordinates={city.coords}
                  onMouseEnter={() => setHoveredCity(key)}
                  onMouseLeave={() => setHoveredCity(null)}
                >
                  <g style={{ cursor: "pointer" }}>
                    <circle
                      r={6}
                      fill={country.color}
                      opacity={0.3}
                    >
                      <animate
                        attributeName="r"
                        values="4;9;4"
                        dur="2.4s"
                        repeatCount="indefinite"
                      />
                      <animate
                        attributeName="opacity"
                        values="0.45;0;0.45"
                        dur="2.4s"
                        repeatCount="indefinite"
                      />
                    </circle>
                    <circle
                      r={isHovered ? 4.5 : 3}
                      fill="#fff"
                      stroke={country.color}
                      strokeWidth={2}
                      style={{ transition: "r 0.2s ease" }}
                    />
                    {isHovered && (
                      <g pointerEvents="none">
                        <rect
                          x={-city.name.length * 2.4}
                          y={-19}
                          width={city.name.length * 4.8}
                          height={13}
                          rx={6.5}
                          fill={COLORS.border}
                        />
                        <text
                          textAnchor="middle"
                          y={-10}
                          style={{
                            fill: "#fff",
                            fontSize: 8,
                            fontWeight: 600,
                            fontFamily: "Inter, sans-serif",
                          }}
                        >
                          {city.name}
                        </text>
                      </g>
                    )}
                  </g>
                </Marker>
              );
            })
          )}

          {/* Country labels */}
          <Marker coordinates={[-102, 24]}>
            <text
              textAnchor="middle"
              style={{
                fill: COLORS.border,
                fontSize: 11,
                fontWeight: 800,
                fontFamily: "Syne, sans-serif",
                letterSpacing: 1.2,
                opacity: 0.85,
              }}
            >
              MÉXICO
            </text>
          </Marker>
          <Marker coordinates={[-89.2, 14.6]}>
            <text
              textAnchor="middle"
              style={{
                fill: COLORS.border,
                fontSize: 6,
                fontWeight: 800,
                fontFamily: "Syne, sans-serif",
                letterSpacing: 0.8,
                opacity: 0.85,
              }}
            >
              EL SALVADOR
            </text>
          </Marker>
          <Marker coordinates={[-73.5, 4]}>
            <text
              textAnchor="middle"
              style={{
                fill: COLORS.border,
                fontSize: 11,
                fontWeight: 800,
                fontFamily: "Syne, sans-serif",
                letterSpacing: 1.2,
                opacity: 0.85,
              }}
            >
              COLOMBIA
            </text>
          </Marker>
        </ZoomableGroup>
      </ComposableMap>
    </div>
  );
});
MapView.displayName = "MapView";

const CountryRow = ({ country, isActive, onHover }) => {
  const { t } = useLanguage();
  return (
    <button
      type="button"
      onMouseEnter={() => onHover(country.code)}
      onMouseLeave={() => onHover(null)}
      data-testid={`country-row-${country.code}`}
      className={`group w-full text-left p-5 rounded-2xl border transition-all duration-300 ${
        isActive
          ? "bg-white border-[#F59E0B]/50 shadow-xl"
          : "bg-white/70 border-[#0B1F3B]/5 hover:bg-white hover:border-[#2ED3C6]/30"
      }`}
    >
      <div className="flex items-start gap-4">
        <span className="text-3xl leading-none mt-0.5" aria-hidden="true">
          {country.flag}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <h4 className="font-display text-lg text-[#0B1F3B] font-semibold">{t(country.name)}</h4>
            {country.flagship && (
              <span className="inline-flex px-2 py-0.5 bg-[#F59E0B] text-[#0B1F3B] text-[9px] font-display font-bold uppercase tracking-wider rounded-full">
                {t(translations.flagshipBadge)}
              </span>
            )}
          </div>
          {country.nationwide ? (
            <p className="font-body text-xs text-[#0B1F3B]/60 mb-2 italic">
              {t(translations.nationwide)}
            </p>
          ) : (
            <p className="font-body text-[10px] text-[#0B1F3B]/45 uppercase tracking-wider mb-2">
              {country.cities.length}{" "}
              {country.cities.length === 1 ? t(translations.cityLabel) : t(translations.citiesLabel)}
            </p>
          )}
          <div className="flex flex-wrap gap-1.5">
            {country.cities.map((city) => (
              <span
                key={city.name}
                className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#0B1F3B]/5 text-[#0B1F3B]/75 text-xs font-body rounded-full"
              >
                <span
                  className="w-1 h-1 rounded-full"
                  style={{ backgroundColor: country.color }}
                />
                {city.name}
              </span>
            ))}
          </div>
        </div>
      </div>
    </button>
  );
};

const TrackRecordSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const { t } = useLanguage();
  const [activeCountry, setActiveCountry] = useState(null);

  return (
    <section
      id="trayectoria"
      data-testid="trackrecord-section"
      ref={ref}
      className="relative py-24 md:py-32 bg-[#EEF1F4] overflow-hidden"
    >
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage: "radial-gradient(circle, #0B1F3B 1px, transparent 1px)",
          backgroundSize: "32px 32px",
        }}
      />

      <div className="relative max-w-6xl mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="text-center mb-14 md:mb-20 max-w-3xl mx-auto"
        >
          <span className="inline-flex items-center gap-2 text-[#0B1F3B] text-xs md:text-sm uppercase tracking-[0.25em] font-bold font-body mb-4">
            <Calendar className="w-4 h-4 text-[#2ED3C6]" />
            {t(translations.overline)}
          </span>
          <h2
            data-testid="trackrecord-title"
            className="font-display text-3xl md:text-4xl lg:text-5xl text-[#0B1F3B] font-medium tracking-tight mb-5"
          >
            {t(translations.title)}
          </h2>
          <p className="font-body text-[#0B1F3B]/65 text-base md:text-lg leading-relaxed">
            {t(translations.subtitle)}
          </p>
        </motion.div>

        {/* Stats grid */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.2 }}
          data-testid="trackrecord-stats"
          className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-16 md:mb-20"
        >
          {STATS.map((stat, i) => {
            const Icon = stat.icon;
            return (
              <motion.div
                key={stat.key}
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.3 + i * 0.1 }}
                className="bg-white rounded-2xl p-6 md:p-7 border border-[#0B1F3B]/5 hover:shadow-xl hover:border-[#2ED3C6]/30 transition-all duration-300"
              >
                <div
                  className="w-11 h-11 rounded-xl flex items-center justify-center mb-4"
                  style={{ backgroundColor: `${stat.color}20` }}
                >
                  <Icon className="w-5 h-5" style={{ color: stat.color }} />
                </div>
                <div className="font-display text-3xl md:text-4xl text-[#0B1F3B] font-bold tracking-tight mb-1">
                  {stat.value}
                </div>
                <div className="font-body text-[#0B1F3B]/55 text-sm">
                  {t(translations.stats[stat.key])}
                </div>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Map title */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-center mb-10"
        >
          <h3 className="font-display text-2xl md:text-3xl text-[#0B1F3B] font-semibold mb-3">
            {t(translations.mapTitle)}
          </h3>
          <p className="font-body text-[#0B1F3B]/55 text-base">{t(translations.mapSubtitle)}</p>
        </motion.div>

        {/* Map + country list */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.5 }}
          className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10"
        >
          <div className="lg:col-span-7 bg-white rounded-3xl p-4 md:p-6 border border-[#0B1F3B]/5 shadow-sm">
            <MapView activeCountry={activeCountry} onHoverCountry={setActiveCountry} />
          </div>

          <div className="lg:col-span-5 space-y-3">
            {COUNTRIES.map((country) => (
              <CountryRow
                key={country.code}
                country={country}
                isActive={activeCountry === country.code}
                onHover={setActiveCountry}
              />
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default TrackRecordSection;
